import React, { useEffect, useState, useRef, FormEvent, ReactNode } from "react";
import { GetServerSideProps } from "next";

// === TYPES ===
export interface StatItem {
  label: string;
  value: string;
}

export interface BenefitItem {
  title: string;
  description: string;
}

export interface AgendaDay {
  day: string;
  title: string;
  topics: string[];
}

export interface SpeakerInfo {
  name: string;
  title: string;
  bio: string;
  imageUrl: string;
  credentials: string[];
}

export interface TestimonialItem {
  name: string;
  role: string;
  text: string;
  imageUrl?: string;
}

export interface OfferItem {
  title: string;
  value: string;
  description: string;
}

export interface FAQItem {
  q: string;
  a: string;
}

export interface WebinarLandingContent {
  announcement: string;
  headline: string;
  subheadline: string;
  ctaPrimary: string;
  ctaSecondary?: string;
  eventDate: string;
  eventTime: string;
  countdown: string;
  stats: StatItem[];
  benefitsHeadline: string;
  benefits: BenefitItem[];
  agendaHeadline: string;
  agenda: AgendaDay[];
  speakerHeadline: string;
  speaker: SpeakerInfo;
  testimonialsHeadline: string;
  testimonials: TestimonialItem[];
  offerHeadline: string;
  offerSubheadline: string;
  offers: OfferItem[];
  totalValue: string;
  currentPrice: string;
  originalPrice: string;
  faqHeadline: string;
  faq: FAQItem[];
  formHeadline: string;
  formSubheadline: string;
  formCtaLabel: string;
  stickyCtaLabel: string;
  footerDisclaimer: string;
  footerCopyright: string;
}

// === CONTENT ===
export const webinarContent: WebinarLandingContent = {
  announcement: "🌸 Limited seats — Over 5,000 women have already secured their spot. Don't miss out!",

  headline: "Protect Your Family & Attract Lifelong Prosperity",
  subheadline:
    "A premium 2-day live masterclass exclusively for women. Learn ancient astrological remedies to shield your loved ones from negative energy, invite abundance, and create a truly harmonious home.",
  ctaPrimary: "Reserve Your Seat — Just ₹99",
  ctaSecondary: "Watch the free intro",

  eventDate: "May 10 – 11, 2026",
  eventTime: "7:00 PM IST",
  countdown: "2026-05-10T13:30:00Z",

  stats: [
    { label: "Families Guided", value: "25,000+" },
    { label: "Positive Results", value: "94%" },
    { label: "Hours of Content", value: "10+" },
    { label: "Average Rating", value: "4.9 / 5" },
  ],

  benefitsHeadline: "What You'll Walk Away With",
  benefits: [
    {
      title: "Ancient Family Remedies",
      description: "No complicated rituals — you'll learn simple, powerful practices you can easily incorporate into your daily routine to protect your home.",
    },
    {
      title: "Shield Against Negativity",
      description: "Identify when negative planetary periods (Dashas) are affecting your family's health or wealth, and safely neutralize them.",
    },
    {
      title: "Harmonize Relationships",
      description: "Discover astrological secrets to drastically reduce household arguments and nurture deep, lasting bonds with your loved ones.",
    },
    {
      title: "Live Q&A Access",
      description: "Get your specific family and astrology questions answered directly by the expert — no generic, pre-recorded advice.",
    },
    {
      title: "Lifetime Replay Access",
      description: "Busy at home? All VIP ticket holders get permanent access to the session recordings to watch at your own pace.",
    },
    {
      title: "Private Women-Only Community",
      description: "Join an exclusive group of like-minded women for ongoing support, spiritual networking, and accountability.",
    },
  ],

  agendaHeadline: "Your 2-Day Transformation Roadmap",
  agenda: [
    {
      day: "Day 1",
      title: "Cosmic Foundation & Home Energy",
      topics: [
        "Understanding core planetary influences on your family",
        "How to detect hidden negative energies in your household",
        "Building a protective energy shield for your children",
        "Interactive exercises + live planetary assessment",
      ],
    },
    {
      day: "Day 2",
      title: "Prosperity Rituals & Lifelong Remedies",
      topics: [
        "Simple, potent remedies for health, wealth, and peace",
        "Creating your personalized 3-month family prosperity blueprint",
        "Common mistakes that block abundance and how to avoid them",
        "Live Q&A + personalized guidance session",
      ],
    },
  ],

  speakerHeadline: "Meet Your Expert Guide",
  speaker: {
    name: "Dr. Arjun Mehta",
    title: "Master Vedic Astrologer & Spiritual Guide",
    bio: "With over 18 years of hands-on experience, Dr. Mehta has helped thousands of families restore peace, health, and prosperity through profound Vedic wisdom. His specific focus on empowering women as the spiritual anchors of their households has transformed over 25,000 homes. He is known for making ancient, complex astrology simple, practical, and incredibly effective for modern life.",
    imageUrl: "https://i.pravatar.cc/400?img=11",
    credentials: [
      "Award-Winning Vedic Scholar",
      "Over 25,000 Families Guided",
      "TEDx Speaker — Spiritual Wellness",
      "Creator of the 'Prosperous Home' Framework",
    ],
  },

  testimonialsHeadline: "Transformations from Incredible Women",
  testimonials: [
    {
      name: "Priya Sharma",
      role: "Mother & Entrepreneur",
      text: "I was dealing with constant health issues in the family and non-stop stress. The remedies I learned were so simple to do. Within a month, the atmosphere at home completely changed.",
      imageUrl: "https://i.pravatar.cc/100?img=5",
    },
    {
      name: "Sneha Verma",
      role: "Homemaker",
      text: "This masterclass gave me the spiritual clarity I'd been searching for. I finally understand how to shield my children's energy. I feel so much more confident and at peace.",
      imageUrl: "https://i.pravatar.cc/100?img=12",
    },
    {
      name: "Anita Desai",
      role: "Teacher",
      text: "The live Q&A was incredibly touching. Dr. Mehta answered my question with such warmth and gave me advice I couldn't have gotten anywhere else. The women's community is a huge blessing.",
      imageUrl: "https://i.pravatar.cc/100?img=9",
    },
  ],

  offerHeadline: "Everything You Get Today",
  offerSubheadline: "Here's the full breakdown of what's included with your VIP pass:",
  offers: [
    { title: "2-Day Live Interactive Masterclass", value: "₹4,999", description: "10+ hours of expert-led, hands-on astrological training." },
    { title: "Personalized Prosperity Blueprint", value: "₹1,999", description: "Leave with a custom remedy roadmap for your family." },
    { title: "Lifetime Recording Access", value: "₹2,999", description: "Revisit sessions anytime, whenever your schedule allows." },
    { title: "Private VIP Women's Community", value: "₹3,999", description: "6 months of ongoing spiritual support and networking." },
    { title: "Bonus: Quick-Start Remedy Kit", value: "₹999", description: "Cheat sheets and step-by-step guides for everyday protection." },
  ],
  totalValue: "₹14,995",
  currentPrice: "₹99",
  originalPrice: "₹4,999",

  faqHeadline: "Frequently Asked Questions",
  faq: [
    { q: "Is this suitable for beginners who know nothing about astrology?", a: "Absolutely. We start from the absolute basics. No prior spiritual or astrological experience is required — just bring an open heart." },
    { q: "Will I get recordings if my children or schedule keep me busy?", a: "Yes. All VIP pass holders receive lifetime access to the full session recordings delivered to your email within 24 hours." },
    { q: "What if this isn't for me?", a: "We offer a 100% money-back guarantee. If you feel the session didn't deliver value for you and your family, just email us within 48 hours for a full refund." },
    { q: "Are the remedies difficult or expensive to perform?", a: "Not at all. The focus is on practical, everyday rituals using items you likely already have in your kitchen or home." },
    { q: "Do I need to prepare anything?", a: "Just bring a notebook, a peaceful state of mind, and a stable internet connection. We'll handle the rest." },
  ],

  formHeadline: "Secure Your Spot Now",
  formSubheadline: "Fill out the form below to lock in your discounted VIP pass.",
  formCtaLabel: "Complete My Registration →",

  stickyCtaLabel: "Reserve Your Seat — ₹99",

  footerDisclaimer: "This site is not a part of the Facebook website or Facebook Inc. Additionally, this site is NOT endorsed by Facebook in any way. FACEBOOK is a trademark of META, Inc. Results may vary. This masterclass provides educational content only and does not guarantee specific life outcomes.",
  footerCopyright: "© 2026 Your Company. All rights reserved.",
};


// === COMPONENTS ===

function SmoothScroll() {
  const animating = useRef(false);

  useEffect(() => {
    function easeInOutCubic(t: number): number {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function smoothScrollTo(targetY: number, duration = 800) {
      if (animating.current) return;
      animating.current = true;

      const startY = window.scrollY;
      const diff = targetY - startY;
      if (Math.abs(diff) < 2) {
        animating.current = false;
        return;
      }

      let start: number | null = null;

      function step(timestamp: number) {
        if (!start) start = timestamp;
        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);

        window.scrollTo(0, startY + diff * easedProgress);

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          animating.current = false;
        }
      }

      requestAnimationFrame(step);
    }

    const handleHashClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");

      const href = anchor?.getAttribute("href");
      if (href && href.startsWith("#") && href.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const element = document.querySelector(href) as HTMLElement;
        if (element) {
          const targetY =
            element.getBoundingClientRect().top + window.scrollY + 55;

          smoothScrollTo(targetY);

          window.history.pushState(null, "", href);
        }
      }
    };

    document.addEventListener("click", handleHashClick, { capture: true });
    return () =>
      document.removeEventListener("click", handleHashClick, { capture: true });
  }, []);

  return null;
}

function SectionWrapper({
  children,
  className = "",
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section
      id={id}
      className={`relative py-20 px-6 md:px-12 lg:px-20 overflow-hidden ${className}`}
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-rose-50/30 to-white" />

      <div className="absolute -top-24 -left-24 w-96 h-96 bg-rose-200/40 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-amber-100/50 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

function Countdown({ targetDate }: { targetDate: string }) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const target = new Date(targetDate).getTime();

    function tick() {
      const distance = target - Date.now();
      if (distance <= 0) return;
      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    }

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return (
    <div className="flex justify-center gap-3 sm:gap-6 mt-6">
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="flex flex-col items-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center rounded-xl bg-white/80 backdrop-blur-sm border border-rose-100 shadow-[0_4px_16px_rgba(225,29,72,0.04)] text-2xl sm:text-3xl font-serif font-semibold text-rose-900 tabular-nums">
            {value.toString().padStart(2, "0")}
          </div>
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-medium mt-3 text-rose-950/60">
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
}

function AnnouncementBar({ text }: { text: string }) {
  if (!text) return null;
  return (
    <div className="bg-rose-950 border-b border-rose-900 text-amber-50 py-3 px-4 text-center text-xs sm:text-sm font-medium tracking-wide">
      {text}
    </div>
  );
}

function Hero({ content }: { content: WebinarLandingContent }) {
  return (
    <SectionWrapper className="!pt-16 !pb-12 md:!pt-24 md:!pb-20">
      <div className="text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-50 text-rose-800 text-sm font-medium mb-8 border border-rose-200/80 shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-[pulse_2s_ease-in-out_infinite]" />
          {content.eventDate} &middot; {content.eventTime}
        </div>

        <h1 className="text-5xl sm:text-6xl md:text-7xl font-serif font-medium tracking-tight text-rose-950 leading-[1.1] whitespace-pre-line">
          {content.headline}
        </h1>

        <p className="mt-8 text-lg md:text-xl text-rose-950/70 max-w-2xl mx-auto leading-relaxed">
          {content.subheadline}
        </p>

        <div className="mt-10">
          <Countdown targetDate={content.countdown} />
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#register"
            className="w-full sm:w-auto inline-flex items-center justify-center px-10 py-4 rounded-xl bg-gradient-to-r from-rose-800 to-rose-900 text-white text-lg font-medium shadow-[0_4px_20px_rgba(225,29,72,0.2)] hover:from-rose-700 hover:to-rose-800 hover:shadow-[0_8px_30px_rgba(225,29,72,0.3)] active:scale-[0.98] transition-all duration-300"
          >
            {content.ctaPrimary}
          </a>
          {content.ctaSecondary && (
            <a
              href="#benefits"
              className="text-rose-700/70 hover:text-rose-900 text-sm font-medium underline underline-offset-4 transition-colors"
            >
              {content.ctaSecondary}
            </a>
          )}
        </div>

        <p className="mt-5 text-sm text-rose-950/50">
          Regularly{" "}
          <span className="line-through">{content.originalPrice}</span>
          <span className="text-amber-600 font-medium ml-2">
            Save{" "}
            {Math.round(
              100 -
                (parseInt(content.currentPrice.replace(/[^\d]/g, "")) /
                  parseInt(content.originalPrice.replace(/[^\d]/g, ""))) *
                  100
            )}
            % today
          </span>
        </p>
      </div>

      <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6 p-6 sm:p-8 rounded-2xl bg-white/80 backdrop-blur-md border border-rose-100 shadow-[0_4px_30px_rgba(225,29,72,0.04)]">
        {content.stats.map((stat, idx) => (
          <div key={idx} className="text-center">
            <div className="text-3xl md:text-4xl font-serif font-semibold text-rose-900 tracking-tight">
              {stat.value}
            </div>
            <div className="text-xs sm:text-sm text-rose-950/50 uppercase tracking-[0.15em] mt-2 font-medium">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

function Benefits({
  headline,
  items,
}: {
  headline: string;
  items: BenefitItem[];
}) {
  return (
    <SectionWrapper id="benefits">
      <h2 className="text-4xl md:text-5xl font-serif font-medium text-center text-rose-950 tracking-tight">
        {headline}
      </h2>
      <div className="w-16 h-1 bg-amber-300 mx-auto mt-6 mb-12 rounded-full" />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="group p-8 rounded-2xl bg-white/70 backdrop-blur-sm border border-rose-100 shadow-[0_4px_20px_rgba(225,29,72,0.03)] hover:shadow-[0_8px_30px_rgba(225,29,72,0.08)] hover:-translate-y-1 hover:border-rose-200 transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-800 flex items-center justify-center font-serif text-lg mb-6 group-hover:bg-rose-800 group-hover:text-amber-50 transition-colors duration-300">
              {String(idx + 1).padStart(2, "0")}
            </div>
            <h3 className="text-xl font-serif font-medium text-rose-950 mb-3">
              {item.title}
            </h3>
            <p className="text-rose-950/70 leading-relaxed text-[15px]">
              {item.description}
            </p>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

function Agenda({
  headline,
  days,
}: {
  headline: string;
  days: AgendaDay[];
}) {
  return (
    <SectionWrapper className="!bg-rose-50/30">
      <h2 className="text-4xl md:text-5xl font-serif font-medium text-center text-rose-950 tracking-tight">
        {headline}
      </h2>
      <div className="w-16 h-1 bg-amber-300 mx-auto mt-6 mb-12 rounded-full" />

      <div className="max-w-4xl mx-auto space-y-6">
        {days.map((day, idx) => (
          <div
            key={idx}
            className="flex flex-col md:flex-row bg-white rounded-2xl overflow-hidden border border-rose-100 shadow-[0_4px_20px_rgba(225,29,72,0.03)]"
          >
            <div className="md:w-48 bg-rose-50 p-6 flex flex-row md:flex-col items-center md:items-start justify-between md:justify-center border-b md:border-b-0 md:border-r border-rose-100">
              <span className="text-rose-600 font-medium tracking-wide uppercase text-sm">
                {day.day}
              </span>
              <h3 className="text-xl font-serif font-medium text-rose-950 md:mt-2 text-right md:text-left">
                {day.title}
              </h3>
            </div>

            <div className="p-6 md:p-8 flex-1">
              <ul className="space-y-4">
                {day.topics.map((topic, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M5 13l4 4L19 7"
                    />
                    </svg>
                    <span className="text-rose-950/80 leading-relaxed">
                      {topic}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

function SpeakerSection({
  headline,
  speaker,
}: {
  headline: string;
  speaker: SpeakerInfo;
}) {
  return (
    <SectionWrapper>
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-serif font-medium text-center text-rose-950 tracking-tight">
          {headline}
        </h2>
        <div className="w-16 h-1 bg-amber-300 mx-auto mt-6 mb-16 rounded-full" />

        <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-20">
          <div className="relative w-64 h-64 md:w-80 md:h-80 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-tr from-rose-200 to-amber-100 rounded-full blur-xl opacity-60" />
            <img
              src={speaker.imageUrl}
              alt={speaker.name}
              className="relative w-full h-full object-cover rounded-full border-4 border-white shadow-[0_8px_30px_rgba(225,29,72,0.1)]"
            />
          </div>

          <div className="flex-1 text-center md:text-left">
            <h3 className="text-3xl font-serif font-medium text-rose-950">
              {speaker.name}
            </h3>
            <p className="text-amber-600 font-medium tracking-wide uppercase text-sm mt-2 mb-4">
              {speaker.title}
            </p>
            <p className="text-rose-950/70 leading-relaxed mb-6 font-serif text-lg">
              {speaker.bio}
            </p>

            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
              {speaker.credentials.map((cred, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-rose-50 text-rose-800 text-xs font-medium border border-rose-100"
                >
                  <svg
                    className="w-3.5 h-3.5 mr-1.5 text-amber-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {cred}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

function Testimonials({
  headline,
  items,
}: {
  headline: string;
  items: TestimonialItem[];
}) {
  return (
    <SectionWrapper className="!bg-rose-50/30">
      <h2 className="text-4xl md:text-5xl font-serif font-medium text-center text-rose-950 tracking-tight">
        {headline}
      </h2>
      <div className="w-16 h-1 bg-amber-300 mx-auto mt-6 mb-12 rounded-full" />

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {items.map((t, idx) => (
          <div
            key={idx}
            className="p-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-rose-100 shadow-[0_4px_20px_rgba(225,29,72,0.03)] flex flex-col hover:shadow-[0_8px_30px_rgba(225,29,72,0.07)] transition-shadow duration-300"
          >
            <div className="flex gap-0.5 mb-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <svg
                  key={i}
                  className="w-4 h-4 text-amber-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>

            <p className="text-rose-950/80 leading-relaxed text-[15px] flex-1 font-serif italic">
              &ldquo;{t.text}&rdquo;
            </p>

            <div className="mt-6 pt-5 flex items-center gap-3 border-t border-rose-100">
              {t.imageUrl && (
                <img
                  src={t.imageUrl}
                  alt={t.name}
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-rose-100"
                  loading="lazy"
                />
              )}
              <div>
                <p className="text-sm font-semibold text-rose-950">{t.name}</p>
                <p className="text-xs text-rose-950/50">{t.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

function OfferStack({
  headline,
  subheadline,
  offers,
  totalValue,
  currentPrice,
}: {
  headline: string;
  subheadline: string;
  offers: OfferItem[];
  totalValue: string;
  currentPrice: string;
}) {
  return (
    <SectionWrapper>
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-serif font-medium text-center text-rose-950 tracking-tight">
          {headline}
        </h2>
        <p className="text-center text-rose-950/70 mt-4 mb-10 text-lg">{subheadline}</p>

        <div className="bg-white rounded-2xl border border-rose-100 shadow-[0_4px_30px_rgba(225,29,72,0.06)] overflow-hidden divide-y divide-rose-50">
          {offers.map((offer, idx) => (
            <div
              key={idx}
              className="flex flex-col sm:flex-row sm:items-center justify-between p-5 sm:p-6 gap-2"
            >
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <div>
                  <p className="font-medium text-rose-950">{offer.title}</p>
                  <p className="text-sm text-rose-950/50 mt-0.5">
                    {offer.description}
                  </p>
                </div>
              </div>
              <span className="text-sm font-medium text-amber-600/80 tabular-nums whitespace-nowrap sm:ml-4">
                Value: {offer.value}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-8 p-8 rounded-2xl bg-rose-950 text-white text-center shadow-[0_10px_40px_rgba(225,29,72,0.15)] relative overflow-hidden">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-rose-800/50 blur-2xl rounded-full" />
          <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 bg-amber-600/20 blur-2xl rounded-full" />
          
          <div className="relative z-10">
            <p className="text-sm text-rose-200 uppercase tracking-widest font-medium">
              Total Value
            </p>
            <p className="text-3xl md:text-4xl font-serif mt-2 line-through decoration-rose-500 decoration-2 opacity-80">
              {totalValue}
            </p>
            <p className="text-base text-rose-100 mt-4 font-medium tracking-wide">Today, secure your spot for</p>
            <p className="text-6xl md:text-7xl font-bold text-amber-400 mt-2 font-serif tracking-tight">
              {currentPrice}
            </p>
            <a
              href="#register"
              className="mt-8 inline-flex items-center justify-center w-full sm:w-auto px-10 py-4 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-rose-950 font-bold text-lg shadow-[0_4px_20px_rgba(251,191,36,0.3)] hover:from-amber-300 hover:to-amber-400 hover:shadow-[0_8px_30px_rgba(251,191,36,0.4)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
            >
              Protect My Family Now →
            </a>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

function FAQ({
  headline,
  items,
}: {
  headline: string;
  items: FAQItem[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <SectionWrapper className="!bg-rose-50/30">
      <h2 className="text-4xl md:text-5xl font-serif font-medium text-center text-rose-950 tracking-tight">
        {headline}
      </h2>
      <div className="w-16 h-1 bg-amber-300 mx-auto mt-6 mb-12 rounded-full" />

      <div className="max-w-3xl mx-auto space-y-3">
        {items.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="bg-white/80 backdrop-blur-sm rounded-xl border border-rose-100 shadow-[0_2px_12px_rgba(225,29,72,0.03)] overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full flex items-center justify-between px-6 py-5 text-left gap-4 hover:bg-rose-50/50 transition-colors"
                aria-expanded={isOpen}
              >
                <span className="font-medium text-rose-950">{faq.q}</span>
                <svg
                  className={`w-5 h-5 text-rose-400 flex-shrink-0 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <p className="px-6 pb-5 text-rose-950/70 leading-relaxed">
                  {faq.a}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </SectionWrapper>
  );
}

interface FormData {
  name: string;
  email: string;
  phone: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
}

function RegistrationForm({
  headline,
  subheadline,
  ctaLabel,
  onSubmit,
}: {
  headline: string;
  subheadline: string;
  ctaLabel: string;
  onSubmit?: (data: FormData) => void | Promise<void>;
}) {
  const [form, setForm] = useState<FormData>({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  function validate(): boolean {
    const e: FormErrors = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email address.";
    if (!form.phone.trim()) e.phone = "Please enter your phone number.";
    else if (!/^\+?[\d\s\-()]{7,15}$/.test(form.phone))
      e.phone = "Please enter a valid phone number.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      if (onSubmit) {
        await onSubmit(form);
      } else {
        await new Promise((r) => setTimeout(r, 1500));
      }
      setSuccess(true);
    } catch {
      setErrors({ name: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  }

  function handleChange(field: keyof FormData, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  if (success) {
    return (
      <SectionWrapper id="register">
        <div className="max-w-md mx-auto text-center py-12 px-8 bg-white rounded-2xl border border-rose-100 shadow-[0_4px_20px_rgba(225,29,72,0.06)]">
          <div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-serif font-medium text-rose-950 mb-2">
            You're Registered!
          </h3>
          <p className="text-rose-950/70">
            Check your email for the event link. We can't wait to see you there! 🌸
          </p>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper id="register">
      <div className="max-w-md mx-auto">
        <div className="bg-white p-8 sm:p-10 rounded-2xl border border-rose-100 shadow-[0_8px_40px_rgba(225,29,72,0.08)] relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-40 h-40 bg-rose-100/60 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-amber-100/40 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-rose-950 text-center mb-2">
              {headline}
            </h2>
            <p className="text-rose-950/60 text-center text-sm mb-8">
              {subheadline}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <label
                  htmlFor="reg-name"
                  className="block text-sm font-medium text-rose-950/80 mb-1.5"
                >
                  Full Name
                </label>
                <input
                  id="reg-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border bg-rose-50/50 text-rose-950 placeholder-rose-300 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-300 ${
                    errors.name ? "border-red-400 bg-red-50/50" : "border-rose-200"
                  }`}
                  placeholder="Your full name"
                />
                {errors.name && (
                  <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.name}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="reg-email"
                  className="block text-sm font-medium text-rose-950/80 mb-1.5"
                >
                  Email Address
                </label>
                <input
                  id="reg-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border bg-rose-50/50 text-rose-950 placeholder-rose-300 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-300 ${
                    errors.email ? "border-red-400 bg-red-50/50" : "border-rose-200"
                  }`}
                  placeholder="you@example.com"
                />
                {errors.email && (
                  <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.email}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="reg-phone"
                  className="block text-sm font-medium text-rose-950/80 mb-1.5"
                >
                  WhatsApp Number
                </label>
                <input
                  id="reg-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border bg-rose-50/50 text-rose-950 placeholder-rose-300 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-rose-200 focus:border-rose-300 ${
                    errors.phone ? "border-red-400 bg-red-50/50" : "border-rose-200"
                  }`}
                  placeholder="+91 98765 43210"
                />
                {errors.phone && (
                  <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.phone}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-rose-800 to-rose-900 text-white font-semibold text-base shadow-[0_4px_20px_rgba(225,29,72,0.2)] hover:from-rose-700 hover:to-rose-800 hover:shadow-[0_8px_30px_rgba(225,29,72,0.3)] active:scale-[0.98] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing…
                  </span>
                ) : (
                  ctaLabel
                )}
              </button>

              <div className="flex items-center justify-center gap-2 text-xs text-rose-950/40 mt-4">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Secure checkout · 100% money-back guarantee</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

function StickyCTA({ label }: { label: string }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ${
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-full opacity-0 pointer-events-none"
      }`}
    >
      <div className="md:hidden p-3 bg-white/90 backdrop-blur-lg border-t border-rose-100">
        <a
          href="#register"
          className="block w-full py-3.5 rounded-xl bg-gradient-to-r from-rose-800 to-rose-900 text-white text-center font-semibold text-sm shadow-[0_-2px_12px_rgba(225,29,72,0.12)] active:scale-[0.98] transition-transform"
        >
          {label}
        </a>
      </div>

      <div className="hidden md:block p-4 bg-white/80 backdrop-blur-lg border-t border-rose-100 shadow-[0_-4px_20px_rgba(225,29,72,0.06)]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <p className="text-rose-950/80 font-serif text-lg">
            <span className="font-medium">Limited seats available.</span>{" "}
            <span className="text-rose-950/50">Secure your spot before registration closes.</span>
          </p>
          <a
            href="#register"
            className="flex-shrink-0 inline-flex items-center px-8 py-3 rounded-xl bg-gradient-to-r from-rose-800 to-rose-900 text-white font-semibold text-sm shadow-[0_4px_16px_rgba(225,29,72,0.2)] hover:from-rose-700 hover:to-rose-800 hover:shadow-[0_8px_24px_rgba(225,29,72,0.3)] active:scale-[0.98] transition-all duration-300"
          >
            {label}
          </a>
        </div>
      </div>
    </div>
  );
}

// === MAIN PAGE ===

export const getServerSideProps: GetServerSideProps = async (context) => {
  return {
    props: {}, // Can pass webinarContent here if needed from backend
  };
};

export default function WebinarPage() {
  const c = webinarContent;

  return (
    <>
      <SmoothScroll />
      <AnnouncementBar text={c.announcement} />
      <Hero content={c} />
      <Benefits headline={c.benefitsHeadline} items={c.benefits} />
      <Agenda headline={c.agendaHeadline} days={c.agenda} />
      <SpeakerSection headline={c.speakerHeadline} speaker={c.speaker} />
      <Testimonials headline={c.testimonialsHeadline} items={c.testimonials} />
      
      <OfferStack
        headline={c.offerHeadline}
        subheadline={c.offerSubheadline}
        offers={c.offers}
        totalValue={c.totalValue}
        currentPrice={c.currentPrice}
      />
      
      <FAQ headline={c.faqHeadline} items={c.faq} />
      
      <RegistrationForm
        headline={c.formHeadline}
        subheadline={c.formSubheadline}
        ctaLabel={c.formCtaLabel}
      />

      <footer className="bg-rose-50/50 border-t border-rose-100 py-12 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <p className="text-xs text-rose-950/40 leading-relaxed">
            {c.footerDisclaimer}
          </p>
          <p className="text-xs text-rose-950/40">{c.footerCopyright}</p>
        </div>
      </footer>

      <StickyCTA label={c.stickyCtaLabel} />
    </>
  );
}
